import { Component, OnInit, AfterViewInit, Renderer2, Inject } from '@angular/core';
import { ToolSearchParams } from 'src/app/shared/models/toolSearchParams';
import { TableData } from 'src/app/shared/models/tableData';
import { checkForProp } from '../tool-transfer/tool-transfer.functions';
import { DOCUMENT } from '@angular/common';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { MomentService } from 'src/app/service/shared/moment/moment.service';
import { ToolInventoryService } from 'src/app/service/tool-inventory/tool-inventory.service';
import { ToolPermissionsService } from 'src/app/service/auth/tool-permissions.service';

@Component({
  selector: 'app-tool-inventory',
  templateUrl: './tool-inventory.component.html',
  styleUrls: ['./tool-inventory.component.scss'],
})
export class ToolInventoryComponent implements OnInit, AfterViewInit {
  defaultSortOrder = 'toolInventory,id,DESC'

  constructor(
    private toolInventoryService: ToolInventoryService,
    /* tslint:disable-next-line variable-name */
    @Inject(DOCUMENT) public _document,
    public renderer2: Renderer2,
    private loaderService: LoaderService,
    private momentService: MomentService,
    private toolPermissionsService: ToolPermissionsService,
  ) { }

  searchParams: ToolSearchParams = {
    page: 1,
    limit: 50,
    order: this.defaultSortOrder
  };

  toolModelExportParams: ToolSearchParams = {
    embed: '',
  };

  filterOptions = {}

  modelDetail: any = {
    'asset-classes': {},
    categories: {},
    manufacturers: {},
    enabled: {},
  };

  filters: Array<any> = [
    {
      label: 'Tool ID',
      type: 'select2MultipleWithCheck',
      propertyName: 'scope[inventory-toolId]',
      default: '',
      value: 'id',
      model: 'inventory',
      options: [],
      name: 'toolId',
      loadOptions: () => this.loadOptions('toolIds'),
    },
    {
      label: 'Category',
      type: 'select2',
      propertyName: 'scope[inventory-category]',
      default: '',
      model: 'inventory',
      options: [],
      name: 'name',
      loadOptions: () => this.loadOptions('categories'),
    },
    {
      label: 'Responsible Party',
      type: 'select2',
      propertyName: 'scope[inventory-responsibleParty]',
      default: '',
      model: 'inventory',
      options: [],
      name: 'employeeAlias',
      loadOptions: () => this.loadOptions('responsibleParties'),
    },
    {
      label: 'Inventoried By',
      type: 'select',
      propertyName: 'scope[inventory-inventoriedBy]',
      default: true,
      model: 'inventory',
      name: 'employeeAlias',
      loadOptions: () => this.loadOptions('inventoriedByUsers'),
    },
    {
      label: 'Batch Number',
      type: 'select2',
      propertyName: 'scope[inventory-inventoryBatchNumber]',
      default: '',
      value: 'batchId',
      model: 'inventory',
      options: [],
      name: 'batchId',
      loadOptions: () => this.loadOptions('batchNumbers'),
    },
    {
      label: 'Date Inventoried',
      type: 'date-range',
      propertyName: 'scope[inventory-dateRange]',
      default: '',
      value: '',
      model: 'dateInventoried',
      options: [],
      name: 'name',
      loadOptions: () => undefined,
    },
    {
      label: 'Assigned To',
      type: 'select2',
      propertyName: 'scope[inventory-assignedTo]',
      default: '',
      value: 'id',
      model: 'inventory',
      options: [],
      name: 'employeeAlias',
      loadOptions: () => this.loadOptions('assignedTo'),
    },
  ];

  tableData: TableData = {
    label: 'Inventory',
    type: 'inventories',
    selectable: true,
    columns: [
      {
        name: 'toolInventory,id',
        label: 'Batch Number',
        sortable: true,
        property: x => checkForProp(x, 'toolInventory.inventoryBatchNumber'),
        headerStyles: { 'min-width': '130px', 'max-width': '140px' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'toolInventory,inventoriedDate',
        label: 'Inventoried',
        sortable: true,
        property: x => {
          const date = x.toolInventory.inventoriedDate
          return this.momentService.formateDate(date)
        },
        headerStyles: { 'min-width': '110px', 'max-width': '120px' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'tool,toolId',
        label: 'Tool ID',
        sortable: true,
        property: x => checkForProp(x, 'tool.toolId'),
        columnStyles: { 'min-width': '80px', 'max-width': '90px' },
        columnClasses: { 'text-truncate': true },
        show: row => {
          return this.toolPermissionsService.isAllowed(row, 'tools', 'detailed_view');
        },
        link: row => {
          return [`/tools/edit/${row.tool.id}`];
        },
      },
      {
        name: 'tool,model,name',
        label: 'Tool Name',
        sortable: true,
        property: x => checkForProp(x, 'tool.model.name'),
        columnStyles: { 'min-width': '160px', 'max-width': '200px' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'tool,model,category,name',
        label: 'Category',
        sortable: true,
        property: x => checkForProp(x, 'tool.model.category.name'),
        columnStyles: { 'min-width': '120px', 'max-width': '150px' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'assignment,assignedTo',
        label: 'Assigned To',
        sortable: true,
        property: x => checkForProp(x, 'assignment.assignedTo'),
        columnStyles: { 'min-width': '180px', 'max-width': '220px' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'assignment,responsibleParty,employeeAlias',
        label: 'Resp. Party',
        sortable: true,
        property: x => checkForProp(x, 'assignment.responsibleParty.employeeAlias'),
        columnStyles: { 'min-width': '150px', 'max-width': '180px' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'toolInventory,inventoriedByUser,employeeAlias',
        label: 'Inventoried By',
        sortable: true,
        property: x => checkForProp(x, 'toolInventory.inventoriedByUser.employeeAlias'),
        columnStyles: { 'min-width': '150px', 'max-width': '180px' },
        columnClasses: { 'text-truncate': true },
      },
      {
        name: 'toolInventory,notes',
        label: 'Inventory Notes',
        sortable: false,
        property: x => checkForProp(x, 'toolInventory.notes'),
        columnStyles: { 'min-width': '150px', 'max-width': '200px' },
        columnClasses: { 'text-truncate': true },
      },
    ],
    rowActions: [],
    data: [],
    pagination: {},
    tableClasses: 'tool-settings-table-for-inventory',
  };


  dialogRecord: any = {};

  get attributes() {
    const attributes = [
      'tool.toolId',
      'tool.model.name',
      'tool.model.category.name',
      'tool.assignment.assignedTo',
      'tool.assignment.responsibleParty.employeeAlias',
      'toolInventory.inventoryBatchNumber',
      'toolInventory.inventoriedDate',
      'toolInventory.inventoriedByUser.employeeAlias',
      'toolInventory.notes',
    ];
    return attributes.join(',');
  }

  get searchFields() {
    const fields = [
      'tool.toolId',
      'tool.model.name',
      'tool.model.category.name',
      'tool.assignment.assignedTo',
      'tool.assignment.responsibleParty.employeeAlias',
      'toolInventory.notes',
    ];
    return fields.join(',');
  }

  ngOnInit(): void {
    this.loaderService.displayLoader();

    this.loadTableFilters()
  }

  hasFilterOptions(): boolean {
    return !!Object.keys(this.filterOptions)?.length
  }

  loadTableFilters() {
    this.toolInventoryService.getToolInventoryFilters().subscribe(res => {
      this.filterOptions = res.data

      // TODO: Show error toast if cannot get filters.
    })
  }

  loadOptions(key: string): void {
    return this.filterOptions[key]
  }

  ngAfterViewInit(): void {
    this.loadSelect2();
  }

  loadSelect2() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `loadSelect2();`;
    this.renderer2.appendChild(this._document.body, s);
  }
}
